// src/pages/Procurement/DispatchedPOViewer.jsx
import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/axios";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { useUserPermissions } from "@/hooks/useUserPermissions";
import { useAuth } from "@/context/AuthProvider";

const NEW_PO_FORM_PATH = "/procurement/new-po";

export default function DispatchedPOViewer() {
  const { isFinance, isAdmin, isDeveloper, isProcurement } = useUserPermissions();
  const { user } = useAuth();
  const navigate = useNavigate();

  const canView = isFinance || isAdmin || isDeveloper || isProcurement;

  const [activeTab, setActiveTab] = useState("drafts");

  const [loadingDrafts, setLoadingDrafts] = useState(true);
  const [loadingDispatched, setLoadingDispatched] = useState(true);

  const [draftPOs, setDraftPOs] = useState([]);
  const [dispatchedPOs, setDispatchedPOs] = useState([]);

  const [searchTerm, setSearchTerm] = useState("");
  const [deletingDraftId, setDeletingDraftId] = useState(null);

  const fetchDrafts = async () => {
    if (!canView) return;

    setLoadingDrafts(true);

    try {
      const res = await api.get("/purchase-orders/drafts", {
        params: { _ts: Date.now() },
      });

      setDraftPOs(Array.isArray(res?.data) ? res.data : []);
    } catch (err) {
      console.error("Draft PO load error:", err);
      toast.error("Failed to load draft POs");
      setDraftPOs([]);
    } finally {
      setLoadingDrafts(false);
    }
  };

  const fetchDispatched = async () => {
    if (!canView) return;

    setLoadingDispatched(true);

    try {
      const res = await api.get("/purchase-orders/dispatched", {
        params: { _ts: Date.now() },
      });

      setDispatchedPOs(Array.isArray(res?.data) ? res.data : []);
    } catch (err) {
      console.error("Dispatched PO load error:", err);
      toast.error("Failed to load dispatched POs");
      setDispatchedPOs([]);
    } finally {
      setLoadingDispatched(false);
    }
  };

  useEffect(() => {
    if (!canView) return;

    fetchDrafts();
    fetchDispatched();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView]);

  const normalizeSearch = (value) => String(value || "").trim().toLowerCase();

  const matchesSearch = (po) => {
    const q = normalizeSearch(searchTerm);
    if (!q) return true;

    const haystack = [
      po.po_id,
      po.po_number,
      po.status,
      po.vendor_name,
      po.event_name,
      po.cost_centre_name,
      po.notes,
      po.expected_delivery,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    return haystack.includes(q);
  };

  const filteredDrafts = useMemo(
    () => draftPOs.filter(matchesSearch),
    [draftPOs, searchTerm]
  );

  const filteredDispatched = useMemo(
    () => dispatchedPOs.filter(matchesSearch),
    [dispatchedPOs, searchTerm]
  );

  const fmtDate = (value) => {
    if (!value) return "—";

    try {
      return new Date(value).toLocaleDateString();
    } catch {
      return String(value).slice(0, 10);
    }
  };

  const fmtMoney = (value) => {
    const n = Number(value || 0);
    return `£${n.toFixed(2)}`;
  };

  const getPoTotal = (po) => {
    return (
      po?.totals?.gross_total ??
      po?.totals?.net_total ??
      po?.gross_total ??
      po?.net_total ??
      po?.approximate_net_total ??
      0
    );
  };

  const getPoItemCount = (po) => {
    return po?.totals?.item_count ?? po?.item_count ?? po?.items_total ?? 0;
  };

  const openDraft = (poId) => {
    if (!poId) return;

    navigate(`${NEW_PO_FORM_PATH}?draft_po_id=${poId}&mode=draft`);
  };

  const createNewDraft = () => {
    navigate(NEW_PO_FORM_PATH);
  };

  const handleDeleteDraft = async (po) => {
    if (!po?.po_id) return;

    const label = po.po_number || `#${po.po_id}`;
    const ok = window.confirm(
      `Delete draft PO ${label}?\n\nThis cannot be undone.`
    );

    if (!ok) return;

    setDeletingDraftId(po.po_id);

    try {
      await api.delete(`/purchase-orders/${po.po_id}/draft`);

      setDraftPOs((prev) => prev.filter((row) => row.po_id !== po.po_id));

      toast.success(`🗑️ Draft ${label} deleted`);

      await fetchDrafts();
    } catch (err) {
      console.error("Delete draft failed:", err);
      toast.error(`Delete failed: ${err.response?.data?.error || "Unknown error"}`);
    } finally {
      setDeletingDraftId(null);
    }
  };

  const handleDownload = async (poId) => {
    try {
      const res = await api.get(`/purchase-orders/pdf/${poId}`, {
        params: { disposition: "attachment" },
        responseType: "blob",
      });

      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);

      const cd = res.headers["content-disposition"] || "";
      const hinted =
        res.headers["x-download-filename"] ||
        (cd.match(/filename="?([^"]+)"?/) || [])[1] ||
        `PO_${poId}.pdf`;

      const a = document.createElement("a");
      a.href = url;
      a.download = hinted;
      document.body.appendChild(a);
      a.click();
      a.remove();

      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      console.error("PDF download error:", err);
      toast.error(`Download failed: ${err.response?.data?.error || "Unknown error"}`);
    }
  };

  const handleResend = async (poId) => {
    try {
      await api.post("/po-dispatch/send", {
        po_id: poId,
        user_id: user?.id ?? user?.user_id ?? null,
      });

      toast.success(`📤 PO #${poId} resent to vendor`);
    } catch (err) {
      console.error("Resend failed:", err);
      toast.error(`Resend failed: ${err.response?.data?.error || "Unknown error"}`);
    }
  };

  const refreshCurrentTab = async () => {
    if (activeTab === "drafts") {
      await fetchDrafts();
      return;
    }

    await fetchDispatched();
  };

  if (!canView) {
    return (
      <div className="p-6 text-red-400">
        <h2 className="text-2xl font-bold">🚫 Access Denied</h2>
        <p>You do not have permission to view purchase orders.</p>
      </div>
    );
  }

  const draftCount = draftPOs.length;
  const dispatchedCount = dispatchedPOs.length;

  return (
    <div className="p-6 text-white max-w-7xl mx-auto">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-cyan-300">
            🧾 Procurement Purchase Orders
          </h1>
          <p className="text-slate-400 text-sm mt-1">
            Manage draft purchase orders and dispatched supplier POs from one place.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-2">
          <button
            type="button"
            onClick={createNewDraft}
            className="bg-emerald-600 hover:bg-emerald-500 px-4 py-2 rounded-lg text-sm font-semibold shadow"
          >
            + New PO / Draft
          </button>

          <button
            type="button"
            onClick={refreshCurrentTab}
            className="bg-slate-700 hover:bg-slate-600 px-4 py-2 rounded-lg text-sm font-semibold shadow"
          >
            Refresh
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
        <div className="rounded-xl border border-cyan-700/50 bg-slate-900 p-4 shadow">
          <div className="text-slate-400 text-sm">Draft POs</div>
          <div className="text-2xl font-bold text-cyan-300">{draftCount}</div>
          <div className="text-xs text-slate-500 mt-1">
            Editable forecasting and unfinished purchase orders
          </div>
        </div>

        <div className="rounded-xl border border-indigo-700/50 bg-slate-900 p-4 shadow">
          <div className="text-slate-400 text-sm">Dispatched / Operational POs</div>
          <div className="text-2xl font-bold text-indigo-300">{dispatchedCount}</div>
          <div className="text-xs text-slate-500 mt-1">
            Approved, dispatched, receiving, and completed supplier POs
          </div>
        </div>

        <div className="rounded-xl border border-emerald-700/50 bg-slate-900 p-4 shadow">
          <div className="text-slate-400 text-sm">Draft Forecast Value</div>
          <div className="text-2xl font-bold text-emerald-300">
            {fmtMoney(
              draftPOs.reduce((sum, po) => sum + Number(getPoTotal(po) || 0), 0)
            )}
          </div>
          <div className="text-xs text-slate-500 mt-1">
            Based on current draft line totals
          </div>
        </div>
      </div>

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-5">
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setActiveTab("drafts")}
            className={`px-4 py-2 rounded-lg text-sm font-semibold border ${
              activeTab === "drafts"
                ? "bg-cyan-600 border-cyan-400 text-white"
                : "bg-slate-900 border-slate-700 text-slate-300 hover:bg-slate-800"
            }`}
          >
            Drafts ({draftCount})
          </button>

          <button
            type="button"
            onClick={() => setActiveTab("dispatched")}
            className={`px-4 py-2 rounded-lg text-sm font-semibold border ${
              activeTab === "dispatched"
                ? "bg-indigo-600 border-indigo-400 text-white"
                : "bg-slate-900 border-slate-700 text-slate-300 hover:bg-slate-800"
            }`}
          >
            Dispatched ({dispatchedCount})
          </button>
        </div>

        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search PO number, vendor, event, status..."
          className="w-full md:w-96 rounded-lg bg-slate-900 border border-slate-700 px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500"
        />
      </div>

      {activeTab === "drafts" ? (
        <DraftTable
          loading={loadingDrafts}
          rows={filteredDrafts}
          fmtDate={fmtDate}
          fmtMoney={fmtMoney}
          getPoTotal={getPoTotal}
          getPoItemCount={getPoItemCount}
          openDraft={openDraft}
          handleDeleteDraft={handleDeleteDraft}
          deletingDraftId={deletingDraftId}
        />
      ) : (
        <DispatchedTable
          loading={loadingDispatched}
          rows={filteredDispatched}
          fmtDate={fmtDate}
          fmtMoney={fmtMoney}
          getPoTotal={getPoTotal}
          getPoItemCount={getPoItemCount}
          handleDownload={handleDownload}
          handleResend={handleResend}
        />
      )}
    </div>
  );
}

function DraftTable({
  loading,
  rows,
  fmtDate,
  fmtMoney,
  getPoTotal,
  getPoItemCount,
  openDraft,
  handleDeleteDraft,
  deletingDraftId,
}) {
  if (loading) {
    return <p className="text-gray-300">Loading draft purchase orders…</p>;
  }

  if (!rows.length) {
    return <p className="text-gray-400 italic">No draft POs found.</p>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full table-auto text-left border-collapse border border-slate-700 bg-slate-900 shadow-xl rounded-lg">
        <thead className="bg-slate-800 text-slate-300">
          <tr>
            <th className="px-4 py-3 border-b border-slate-700">PO #</th>
            <th className="px-4 py-3 border-b border-slate-700">Vendor</th>
            <th className="px-4 py-3 border-b border-slate-700">Event</th>
            <th className="px-4 py-3 border-b border-slate-700">Delivery</th>
            <th className="px-4 py-3 border-b border-slate-700">Items</th>
            <th className="px-4 py-3 border-b border-slate-700">Forecast Total</th>
            <th className="px-4 py-3 border-b border-slate-700">Created</th>
            <th className="px-4 py-3 border-b border-slate-700">Actions</th>
          </tr>
        </thead>

        <tbody>
          {rows.map((po) => {
            const isDeleting = deletingDraftId === po.po_id;

            return (
              <tr key={po.po_id} className="hover:bg-slate-800 border-b border-slate-700">
                <td className="px-4 py-3 text-cyan-400 font-semibold">
                  {po.po_number || po.po_id}
                  <div className="text-xs text-slate-500">Draft</div>
                </td>

                <td className="px-4 py-3">{po.vendor_name || "Not selected"}</td>

                <td className="px-4 py-3">
                  {po.event_name || (po.event_id ? `Event #${po.event_id}` : "—")}
                </td>

                <td className="px-4 py-3">{fmtDate(po.expected_delivery)}</td>

                <td className="px-4 py-3">{getPoItemCount(po)}</td>

                <td className="px-4 py-3 font-semibold text-emerald-300">
                  {fmtMoney(getPoTotal(po))}
                </td>

                <td className="px-4 py-3">{fmtDate(po.created_at)}</td>

                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => openDraft(po.po_id)}
                      className="bg-cyan-700 hover:bg-cyan-600 px-3 py-1 text-sm rounded shadow disabled:opacity-50"
                      type="button"
                      disabled={isDeleting}
                    >
                      Open Draft
                    </button>

                    <button
                      onClick={() => handleDeleteDraft(po)}
                      className="bg-red-700 hover:bg-red-600 px-3 py-1 text-sm rounded shadow disabled:opacity-50"
                      type="button"
                      disabled={isDeleting}
                    >
                      {isDeleting ? "Deleting…" : "Delete"}
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function DispatchedTable({
  loading,
  rows,
  fmtDate,
  fmtMoney,
  getPoTotal,
  getPoItemCount,
  handleDownload,
  handleResend,
}) {
  if (loading) {
    return <p className="text-gray-300">Loading dispatched purchase orders…</p>;
  }

  if (!rows.length) {
    return <p className="text-gray-400 italic">No dispatched POs found.</p>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full table-auto text-left border-collapse border border-slate-700 bg-slate-900 shadow-xl rounded-lg">
        <thead className="bg-slate-800 text-slate-300">
          <tr>
            <th className="px-4 py-3 border-b border-slate-700">PO #</th>
            <th className="px-4 py-3 border-b border-slate-700">Status</th>
            <th className="px-4 py-3 border-b border-slate-700">Vendor</th>
            <th className="px-4 py-3 border-b border-slate-700">Event</th>
            <th className="px-4 py-3 border-b border-slate-700">Delivery</th>
            <th className="px-4 py-3 border-b border-slate-700">Items</th>
            <th className="px-4 py-3 border-b border-slate-700">Value</th>
            <th className="px-4 py-3 border-b border-slate-700">Actions</th>
          </tr>
        </thead>

        <tbody>
          {rows.map((po) => (
            <tr key={po.po_id} className="hover:bg-slate-800 border-b border-slate-700">
              <td className="px-4 py-3 text-cyan-400 font-semibold">
                {po.po_number || po.po_id}
              </td>

              <td className="px-4 py-3">
                <span className="rounded-full bg-indigo-900/70 text-indigo-200 border border-indigo-500/40 px-2 py-1 text-xs">
                  {po.status || "—"}
                </span>
              </td>

              <td className="px-4 py-3">{po.vendor_name || "—"}</td>

              <td className="px-4 py-3">
                {po.event_name || (po.event_id ? `Event #${po.event_id}` : "—")}
              </td>

              <td className="px-4 py-3">{fmtDate(po.expected_delivery)}</td>

              <td className="px-4 py-3">
                {po.items_fulfilled != null && po.items_total != null
                  ? `${po.items_fulfilled}/${po.items_total}`
                  : getPoItemCount(po)}
              </td>

              <td className="px-4 py-3 font-semibold text-emerald-300">
                {fmtMoney(getPoTotal(po))}
              </td>

              <td className="px-4 py-3 space-x-2">
                <button
                  onClick={() => handleDownload(po.po_id)}
                  className="bg-blue-700 hover:bg-blue-600 px-3 py-1 text-sm rounded shadow"
                  type="button"
                >
                  Download
                </button>

                <button
                  onClick={() => handleResend(po.po_id)}
                  className="bg-indigo-600 hover:bg-indigo-500 px-3 py-1 text-sm rounded shadow"
                  type="button"
                >
                  Resend
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}